import { Injectable } from '@angular/core';
import { AutoSub, AutoSubOnDestroy, Media, Mote, MoteType, ProjectService, QuizAnswer, TourDetails } from '@motes-sdk';
import { getValue } from '@motes-sdk/utils/functions';
import { RecursiveKeyOf } from '@motes-sdk/utils/types';
import { Observable, ReplaySubject } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';

export type RuleId =
    'titleNotEmpty' |
    'teaserNotEmpty' |
    'hasCoverImage' |
    'storyTextNotEmpty' |
    'quizQuestionNotEmpty' |
    'quizAnswersNotEmpty' |
    'hasCorrectAnswers' |
    'startPlaceNotEmpty' |
    'endPlaceNotEmpty' |
    'durationNotEmpty' |
    'cityNotEmpty' |
    'countryNotEmpty';

interface Rule {
    id: RuleId;
    type: MoteType[];
    property: string; // RecursiveKeyOf<Mote>;
    validator: Validator<unknown>;
    condition?: {
        property: string; // RecursiveKeyOf<Mote>;
        validator: Validator<unknown>;
    };
}

type Validator<T> = (value: T) => boolean;
type Validators = { [key: string]: Validator<unknown> | ((unknown) => Validator<unknown>) | Validators; };

interface IsValidResult {
    isValid: boolean;
    progress: number;
    showError: boolean;
};

type MoteMap = { [moteKey: string]: IsValidResult; };
type TestResult = { didSucceed: boolean, showError: boolean; };
type TestResultMap = { [ruleId in RuleId]?: TestResult; };
type SuppressedErrors = { [moteKey: string]: Set<RuleId>; };

@AutoSubOnDestroy()
@Injectable({
    providedIn: 'root',
})
export class TourValidationService {
    private readonly validators = {
        string: {
            notEmpty: (value: string) => Boolean(value),
        },
        boolean: {
            is: (valueToTest: boolean) => (value: boolean) => valueToTest == value,
        },
        quiz: {
            correctAnswers: (min: number = 0, max: number = Number.MAX_SAFE_INTEGER) => (quizAnswers: QuizAnswer[]) => {
                const correctAmount = quizAnswers.filter((answer) => answer.isCorrect).length;
                return correctAmount >= min && correctAmount <= max;
            },
        },
        array: {
            propertyNotEmpty: (property: string) => (array: object[]) => array.every((el) => el.hasOwnProperty(property) && Boolean(el[property]))
        },
        media: {
            storyHasMainVideoOrAudio: (media: Media) => Boolean(media.sound.url) || Boolean(media.video.url),
        }
    } as const;
    // This is intentionally never read, and is only present to have TS validate type of validators
    // Setting the type directly onto `this.validators` would require type narrowing on the variable make the IDE lose autocompletion
    // * If you get a type error in `_testTS`, it means that you have a type error in `this.validators`
    private _testTS: Validators = this.validators;

    private readonly rules: Rule[] = [
        {
            id: 'titleNotEmpty',
            type: [MoteType.PLACE, MoteType.ROUTE, MoteType.COLLECTION],
            property: 'title',
            validator: this.validators.string.notEmpty,
        },
        {
            id: 'teaserNotEmpty',
            type: [MoteType.PLACE, MoteType.ROUTE, MoteType.COLLECTION],
            property: 'previewText',
            validator: this.validators.string.notEmpty,
        },
        {
            id: 'hasCoverImage',
            type: [MoteType.PLACE, MoteType.ROUTE, MoteType.COLLECTION],
            property: 'image.hd.url',
            validator: this.validators.string.notEmpty,
        },
        {
            id: 'storyTextNotEmpty',
            type: [MoteType.PLACE, MoteType.ROUTE, MoteType.COLLECTION],
            property: 'text',
            validator: this.validators.string.notEmpty,
        },
        {
            id: 'quizQuestionNotEmpty',
            type: [MoteType.PLACE],
            property: 'quiz.question',
            validator: this.validators.string.notEmpty,
            condition: {
                property: 'quiz.disabled',
                validator: this.validators.boolean.is(false),
            },
        },
        {
            id: 'quizAnswersNotEmpty',
            type: [MoteType.PLACE],
            property: 'quiz.answers',
            validator: this.validators.array.propertyNotEmpty('label'),
            condition: {
                property: 'quiz.disabled',
                validator: this.validators.boolean.is(false),
            },
        },
        {
            id: 'hasCorrectAnswers',
            type: [MoteType.PLACE],
            property: 'quiz.answers',
            validator: this.validators.quiz.correctAnswers(1),
            condition: {
                property: 'quiz.disabled',
                validator: this.validators.boolean.is(false),
            },
        },
        {
            id: 'startPlaceNotEmpty',
            type: [MoteType.ROUTE],
            property: 'startPlace',
            validator: this.validators.string.notEmpty
        },
        {
            id: 'endPlaceNotEmpty',
            type: [MoteType.ROUTE],
            property: 'endPlace',
            validator: this.validators.string.notEmpty
        },
        {
            id: 'durationNotEmpty',
            type: [MoteType.ROUTE],
            property: 'duration',
            validator: this.validators.string.notEmpty
        },
        {
            id: 'cityNotEmpty',
            type: [MoteType.ROUTE, MoteType.COLLECTION],
            property: 'city',
            validator: this.validators.string.notEmpty
        },
        {
            id: 'countryNotEmpty',
            type: [MoteType.ROUTE, MoteType.COLLECTION],
            property: 'country',
            validator: this.validators.string.notEmpty
        }
    ];

    private motesToTest: Mote[] = [];

    private moteMapResults = new ReplaySubject<MoteMap>();
    private testResults = new ReplaySubject<{ moteKey: string, testResultMap: TestResultMap; }>();

    private suppressedErrors: SuppressedErrors = {};
    private doughnutErrorBlink: boolean = false;
    private showRuleErrors: boolean = false;

    constructor(
        private projectService: ProjectService,
    ) { }

    public init() {
        // When the active tour changes or is updated, save all motes that needs to be tested (Tour + Stories)
        // into a local variable, so that we are able to re-run tests without mote updates
        AutoSub(this).reg['activeTourSub'] = this.projectService.watchActiveTourDetails()
            .pipe(debounceTime(500))
            .subscribe((tourDetails) => {
                if (!tourDetails) return;
                this.motesToTest = [tourDetails.tour, ...tourDetails.children];
                this.updateMoteMap();
            });
    }

    public updateMoteMap() {
        const moteMap = this.motesToTest.reduce((acc, mote) => {
            acc[mote.key] = this.isValid(mote);
            return acc;
        }, {} as MoteMap);
        this.moteMapResults.next(moteMap);
    }

    private runTests(rules: Rule[], mote: Mote): TestResultMap {
        const testResultMap = rules.reduce((acc, rule) => {
            if(rule.condition) {
                const conditionToTest = getValue(mote, rule.condition.property, null as null);
                const conditionResult = rule.condition.validator(conditionToTest);
                if(!conditionResult) {
                    acc[rule.id] = null;
                    return acc;
                }
            }

            const valueToTest = getValue(mote, rule.property, null as null);
            const didSucceed = rule.validator(valueToTest);
            const errorIsSuppressed = (this.suppressedErrors[mote.key] ?? false) && this.suppressedErrors[mote.key].has(rule.id);
            const shouldShowError = this.showRuleErrors && !errorIsSuppressed && !didSucceed;
            const testResult: TestResult = {
                didSucceed: didSucceed,
                showError: shouldShowError,
            };

            acc[rule.id] = testResult;
            return acc;
        }, {} as TestResultMap);

        this.testResults.next({
            moteKey: mote.key,
            testResultMap: testResultMap,
        });
        return testResultMap;
    }

    private isValid(mote: Mote): IsValidResult {
        const applicableValidation = this.rules.filter((rule) => rule.type.some((type) => type == mote.type));

        // if there are no applicable tests, validation is successful
        if(applicableValidation.length == 0) return {
            isValid: true,
            progress: 1,
            showError: false,
        };

        const results = this.runTests(applicableValidation, mote);
        const [succeeded, failed, notApplicable] = Object.entries(results).reduce((acc, [testId, testResult]) => {
            if(testResult == null) {
                acc[2].push(testId);
                return acc;
            }
            testResult.didSucceed ? acc[0].push(testId) : acc[1].push(testId);
            return acc;
        }, [[], [], []]);

        return {
            isValid: failed.length == 0,
            progress: succeeded.length / (applicableValidation.length - notApplicable.length),
            showError: this.doughnutErrorBlink && failed.length > 0,
        };
    }

    public watchAllMotesResults(): Observable<MoteMap> {
        return this.moteMapResults.asObservable();
    }

    public watchMoteTestResults(moteKey: string): Observable<TestResultMap> {
        return this.testResults.pipe(
            filter((testResult) => testResult.moteKey == moteKey),
            map((x) => x.testResultMap),
        );
    }

    public enableErrors() {
        this.doughnutErrorBlink = true;
        this.showRuleErrors = true;
        this.suppressedErrors = {};
        this.updateMoteMap();
    }

    public disableDoughnutErrorBlink() {
        this.doughnutErrorBlink = false;
        this.updateMoteMap();
    }

    public suppressError(moteKey: string, ruleId: RuleId) {
        if(!this.suppressedErrors[moteKey]) this.suppressedErrors[moteKey] = new Set();
        this.suppressedErrors[moteKey].add(ruleId);
        this.updateMoteMap();
    }

    public isTourValid(tourDetails: TourDetails): boolean {
        this.motesToTest = [tourDetails.tour, ...tourDetails.children];
        const moteMap = this.motesToTest.reduce((acc, mote) => {
            acc[mote.key] = this.isValid(mote);
            return acc;
        }, {} as MoteMap);

        return Object.values(moteMap).every((mapValue) => mapValue.isValid);
    }
}
